import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTheme } from '../contexts/ThemeContext';
import { CaseReceivedSummary } from '../types/caseApi';
import { CustomTooltip } from './CustomTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faEye, faClock, faCopy, faSort, faSortUp, faSortDown, faSpinner } from '@/icons';
import { CardTable } from './CardTable';
import { ConfirmationModal } from './Modal';
import { useSingleFlight } from '../hooks/useSingleFlight';

interface ReceivedCasesTableProps {
    receivedCases: CaseReceivedSummary[];
    loading: boolean;
    error: string | null;
    sortConfig: { key: string; direction: 'asc' | 'desc' } | null;
    onSort: (key: string) => void;
    getSortIcon: (key: string) => React.ReactNode;
    acceptingCaseSentId: string | null;
    rejectingCaseSentId: string | null;
    hoveredButton: { caseId: string; button: string } | null;
    onHoverButton: (caseId: string | null, button: string | null) => void;
    onAcceptClick: (caseSentId: string) => void;
    onRejectClick: (caseSentId: string, rejectionNote?: string) => void | Promise<void>;
    /**
     * Copy an accepted case. `requestId` is one per click (see useSingleFlight); reject to keep it
     * for a retry. The button stays disabled until the returned promise settles.
     */
    onCopyClick?: (caseId: string, requestId: string) => void | Promise<void>;
    styles: any; // Styles object from HomePage
}

export const ReceivedCasesTable: React.FC<ReceivedCasesTableProps> = ({
    receivedCases,
    loading,
    error,
    sortConfig,
    onSort,
    getSortIcon,
    acceptingCaseSentId,
    rejectingCaseSentId,
    hoveredButton,
    onHoverButton,
    onAcceptClick,
    onRejectClick,
    onCopyClick,
    styles,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    // The pending row whose Reject was clicked, waiting for the user to confirm.
    const [confirmRejectCaseSentId, setConfirmRejectCaseSentId] = useState<string | null>(null);
    // Optional note sent with the rejection; cleared each time the dialog opens.
    const [rejectNote, setRejectNote] = useState('');
    // One copy at a time: a double click on Copy makes one copy. The row being copied shows a spinner.
    const copyFlight = useSingleFlight();
    const [copyingCaseId, setCopyingCaseId] = useState<string | null>(null);
    const lastCopyCaseIdRef = useRef<string | null>(null);
    const handleCopy = (caseId: string) => {
        if (!onCopyClick || copyFlight.inFlight) return;
        // Another row is another user action.
        if (lastCopyCaseIdRef.current !== caseId) copyFlight.resetRequestId();
        lastCopyCaseIdRef.current = caseId;
        void copyFlight.run(async (requestId) => {
            setCopyingCaseId(caseId);
            try {
                await onCopyClick(caseId, requestId);
            } finally {
                setCopyingCaseId(null);
            }
        }).catch(() => {});
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month} ${day}, ${year}`;
    };

    const renderParties = (receivedCase: CaseReceivedSummary) => {
        const partyA = receivedCase.afn && receivedCase.aln ? `${receivedCase.afn} ${receivedCase.aln}` : 'Party A';
        const partyB = receivedCase.bfn && receivedCase.bln ? `${receivedCase.bfn} ${receivedCase.bln}` : 'Party B';
        const isPetitionerB = receivedCase.pet === 'B';

        if (isPetitionerB) {
            return (
                <div style={styles.partiesContainer}>
                    <div style={styles.partyA}>{partyB}</div>
                    <div style={styles.partyB}>{partyA}</div>
                </div>
            );
        } else {
            return (
                <div style={styles.partiesContainer}>
                    <div style={styles.partyA}>{partyA}</div>
                    <div style={styles.partyB}>{partyB}</div>
                </div>
            );
        }
    };

    const renderReceivedFrom = (receivedCase: CaseReceivedSummary) => {
        if (!receivedCase.rcvFrom) return 'N/A';
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                {receivedCase.rcvFrom.userName && (
                    <div style={{ whiteSpace: 'nowrap' as const }}>
                        {receivedCase.rcvFrom.userName}
                    </div>
                )}
                {receivedCase.rcvFrom.teamName && (
                    <div style={{
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.text.secondary,
                        wordWrap: 'break-word' as const,
                        whiteSpace: 'normal' as const
                    }}>
                        {receivedCase.rcvFrom.teamName}
                    </div>
                )}
            </div>
        );
    };

    const renderAlsoSentTo = (receivedCase: CaseReceivedSummary) => {
        if (receivedCase.sntTo.length === 0) return 'None';
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                {receivedCase.sntTo.map((recipient, idx) => (
                    <div key={idx} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        marginBottom: idx < receivedCase.sntTo.length - 1 ? theme.spacing.xs : 0
                    }}>
                        <div style={{ whiteSpace: 'nowrap' as const }}>
                            {recipient.userName}
                        </div>
                        {recipient.teamName && (
                            <div style={{
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.secondary,
                                wordWrap: 'break-word' as const,
                                whiteSpace: 'normal' as const
                            }}>
                                {recipient.teamName}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const renderStatus = (receivedCase: CaseReceivedSummary) => {
        // Use the exact same status check logic as the action column
        // The action column uses: receivedCase.stat === 'rejected', receivedCase.stat === 'pending' || !receivedCase.stat, receivedCase.stat === 'accepted'
        const statusValue = receivedCase.stat;

        // Determine banner style based on status - check in same order as action column
        let bannerStyle: React.CSSProperties;
        let bannerIcon: any;
        let bannerText: string;

        // Check rejected first (matches action column)
        if (statusValue === 'rejected') {
            bannerStyle = {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.xs,
                padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                backgroundColor: (theme.colors.status?.error || '#dc2626') + '15',
                border: `1px solid ${theme.colors.status?.error || '#dc2626'}`,
                borderRadius: theme.borderRadius.sm,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.status?.error || '#dc2626',
                width: '100%',
                minWidth: '120px',
                marginBottom: theme.spacing.xs,
            };
            bannerIcon = faTimesCircle;
            bannerText = 'Rejected';
        } else if (statusValue === 'accepted') {
            bannerStyle = {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.xs,
                padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                backgroundColor: (theme.colors.status?.success || '#10b981') + '15',
                border: `1px solid ${theme.colors.status?.success || '#10b981'}`,
                borderRadius: theme.borderRadius.sm,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.status?.success || '#10b981',
                width: '100%',
                minWidth: '120px',
                marginBottom: theme.spacing.xs,
            };
            bannerIcon = faCheckCircle;
            bannerText = 'Accepted';
        } else if (statusValue === 'rescinded') {
            bannerStyle = {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.xs,
                padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                backgroundColor: (theme.colors.status?.error || '#dc2626') + '15',
                border: `1px solid ${theme.colors.status?.error || '#dc2626'}`,
                borderRadius: theme.borderRadius.sm,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.status?.error || '#dc2626',
                width: '100%',
                minWidth: '120px',
                marginBottom: theme.spacing.xs,
            };
            bannerIcon = faTimesCircle;
            bannerText = 'Rescinded';
        } else {
            // Pending or any other status (including undefined/null)
            bannerStyle = {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.xs,
                padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                backgroundColor: (theme.colors.status?.warning || '#f59e0b') + '15',
                border: `1px solid ${theme.colors.status?.warning || '#f59e0b'}`,
                borderRadius: theme.borderRadius.sm,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.status?.warning || '#f59e0b',
                width: '100%',
                minWidth: '120px',
                marginBottom: theme.spacing.xs,
            };
            bannerIcon = faClock;
            bannerText = 'Pending';
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                {/* Status banner */}
                <div style={bannerStyle}>
                    {bannerIcon && <FontAwesomeIcon icon={bannerIcon} style={{ fontSize: theme.typography.fontSize.sm }} />}
                    <span>{bannerText}</span>
                </div>

                {/* Last accepted info (if available) */}
                {receivedCase.lsAcc && (
                    <>
                        {receivedCase.lsAcc.accDate && (
                            <div style={{ whiteSpace: 'nowrap' as const, fontSize: theme.typography.fontSize.sm }}>
                                {formatDate(receivedCase.lsAcc.accDate)}
                            </div>
                        )}
                        {receivedCase.lsAcc.accBy && (
                            <div style={{
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.secondary,
                                whiteSpace: 'nowrap' as const
                            }}>
                                by {receivedCase.lsAcc.accBy}
                            </div>
                        )}
                        {receivedCase.lsAcc.isDemo && (
                            <div style={{
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.secondary,
                            }}>
                                (Demo)
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div style={styles.loadingMessage}>
                <img
                    src="/logo-spinner.gif"
                    alt="Loading..."
                    style={styles.loadingSpinner}
                />
                Loading received cases...
            </div>
        );
    }

    if (error) {
        return <div style={styles.errorMessage}>{error}</div>;
    }

    if (receivedCases.length === 0) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: theme.spacing.xl,
                textAlign: 'center',
                color: theme.colors.text.secondary,
            }}>
                <div style={{
                    fontSize: theme.typography.fontSize.xl,
                    marginBottom: theme.spacing.sm,
                }}>
                    <i className="fas fa-inbox" style={{ marginRight: theme.spacing.sm }}></i>
                    No received cases found
                </div>
            </div>
        );
    }

    return (
        <div className="rw-scroll-x">
        <CardTable style={styles.table}>
            <thead>
                <tr style={styles.tableHeader}>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('canAccept')}
                    >
                        Action {getSortIcon('canAccept')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('parties')}
                    >
                        Parties {getSortIcon('parties')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('receivedDate')}
                    >
                        Received Date {getSortIcon('receivedDate')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('receivedFrom')}
                    >
                        Received From {getSortIcon('receivedFrom')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('comments')}
                    >
                        Comments {getSortIcon('comments')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('alsoSentTo')}
                    >
                        Also Sent To {getSortIcon('alsoSentTo')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('status')}
                    >
                        Status {getSortIcon('status')}
                    </th>
                </tr>
            </thead>
            <tbody>
                {receivedCases.map((receivedCase, index) => (
                    <tr key={receivedCase.csid || index} style={{
                        ...styles.tableRow,
                        backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    }}>
                        <td style={styles.cell}>
                            {receivedCase.stat === 'rescinded' ? (
                                <span style={{
                                    color: theme.colors.text.secondary,
                                    display: 'block',
                                    textAlign: 'center',
                                    width: '100%'
                                }}>N/A</span>
                            ) : (
                                <div style={{ display: 'flex', gap: theme.spacing.xs, alignItems: 'center' }}>
                                    {receivedCase.cid && (
                                        <CustomTooltip
                                            text="View case as\noriginally sent"
                                            position="top"
                                            show={hoveredButton?.caseId === receivedCase.csid && hoveredButton?.button === 'view'}
                                        >
                                            <button
                                                style={styles.copyButton}
                                                onMouseEnter={() => onHoverButton(receivedCase.csid, 'view')}
                                                onMouseLeave={() => onHoverButton(null, null)}
                                                onClick={() => {
                                                    if (receivedCase.cid) {
                                                        navigate(`/wizard/${receivedCase.sc ?? 'IA'}/${receivedCase.cid}/case`);
                                                    }
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} style={styles.buttonIcon} />
                                            </button>
                                        </CustomTooltip>
                                    )}
                                    {(receivedCase.stat === 'pending' || !receivedCase.stat) ? (
                                        <div style={{ display: 'flex', gap: theme.spacing.xs, alignItems: 'center' }}>
                                            <CustomTooltip
                                                text="Accept this case,\ncreating an editable copy"
                                                position="top"
                                                show={hoveredButton?.caseId === receivedCase.csid && hoveredButton?.button === 'accept'}
                                            >
                                                <button
                                                    style={{
                                                        ...styles.copyButton,
                                                        backgroundColor: theme.colors.status?.success || '#10b981',
                                                        border: `1px solid ${theme.colors.status?.success || '#10b981'}`,
                                                        cursor: acceptingCaseSentId === receivedCase.csid ? 'not-allowed' : 'pointer',
                                                        opacity: acceptingCaseSentId === receivedCase.csid ? 0.6 : 1,
                                                    }}
                                                    onMouseEnter={() => onHoverButton(receivedCase.csid, 'accept')}
                                                    onMouseLeave={() => onHoverButton(null, null)}
                                                    onClick={() => {
                                                        if (acceptingCaseSentId !== receivedCase.csid) {
                                                            onAcceptClick(receivedCase.csid);
                                                        }
                                                    }}
                                                    disabled={acceptingCaseSentId === receivedCase.csid}
                                                >
                                                    <FontAwesomeIcon icon={faCheckCircle} style={styles.buttonIcon} />
                                                </button>
                                            </CustomTooltip>
                                            <CustomTooltip
                                                text="Reject this case"
                                                position="top"
                                                show={hoveredButton?.caseId === receivedCase.csid && hoveredButton?.button === 'reject'}
                                            >
                                                <button
                                                    aria-label="Reject this case"
                                                    style={{
                                                        ...styles.copyButton,
                                                        backgroundColor: theme.colors.status?.error || '#dc2626',
                                                        border: `1px solid ${theme.colors.status?.error || '#dc2626'}`,
                                                        cursor: rejectingCaseSentId === receivedCase.csid ? 'not-allowed' : 'pointer',
                                                        opacity: rejectingCaseSentId === receivedCase.csid ? 0.6 : 1,
                                                    }}
                                                    onMouseEnter={() => onHoverButton(receivedCase.csid, 'reject')}
                                                    onMouseLeave={() => onHoverButton(null, null)}
                                                    onClick={() => {
                                                        if (rejectingCaseSentId !== receivedCase.csid) {
                                                            setRejectNote('');
                                                            setConfirmRejectCaseSentId(receivedCase.csid);
                                                        }
                                                    }}
                                                    disabled={rejectingCaseSentId === receivedCase.csid}
                                                >
                                                    <FontAwesomeIcon icon={faTimesCircle} style={styles.buttonIcon} />
                                                </button>
                                            </CustomTooltip>
                                        </div>
                                    ) : receivedCase.stat === 'accepted' && receivedCase.cid && onCopyClick ? (
                                        <CustomTooltip
                                            text="Make another editable copy\nof the original sent case"
                                            position="top"
                                            show={hoveredButton?.caseId === receivedCase.csid && hoveredButton?.button === 'copy'}
                                        >
                                            <button
                                                style={copyFlight.inFlight ? { ...styles.copyButton, cursor: 'not-allowed', opacity: 0.6 } : styles.copyButton}
                                                disabled={copyFlight.inFlight}
                                                aria-busy={copyingCaseId === receivedCase.cid}
                                                data-testid="received-copy-button"
                                                onMouseEnter={() => onHoverButton(receivedCase.csid, 'copy')}
                                                onMouseLeave={() => onHoverButton(null, null)}
                                                onClick={() => {
                                                    if (receivedCase.cid) {
                                                        handleCopy(receivedCase.cid);
                                                    }
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={copyingCaseId === receivedCase.cid ? faSpinner : faCopy}
                                                    spin={copyingCaseId === receivedCase.cid}
                                                    style={styles.buttonIcon}
                                                />
                                            </button>
                                        </CustomTooltip>
                                    ) : null}
                                </div>
                            )}
                        </td>
                        <td style={styles.cell}>
                            {(receivedCase.afn || receivedCase.bfn) ? renderParties(receivedCase) : 'N/A'}
                        </td>
                        <td style={styles.cell}>
                            {formatDate(receivedCase.rcvAt)}
                        </td>
                        <td style={styles.cell}>
                            {renderReceivedFrom(receivedCase)}
                        </td>
                        <td style={{ ...styles.cell, verticalAlign: 'top' as const, padding: theme.spacing.xs }}>
                            {receivedCase.cm ? (
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        padding: theme.spacing.xs,
                                        border: `1px solid ${theme.colors.border.default}`,
                                        borderRadius: theme.borderRadius.sm,
                                        fontSize: theme.typography.fontSize.md,
                                        fontFamily: theme.typography.fontFamily,
                                        backgroundColor: '#dbeafe',
                                        color: '#1e40af',
                                        overflowY: 'auto' as const,
                                        overflowX: 'hidden' as const,
                                        wordWrap: 'break-word' as const,
                                        whiteSpace: 'pre-wrap' as const,
                                        lineHeight: theme.typography.lineHeight.normal,
                                        boxSizing: 'border-box' as const,
                                    }}
                                >
                                    {receivedCase.cm}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center' as const, color: theme.colors.text.secondary }}>
                                    No comment provided
                                </div>
                            )}
                        </td>
                        <td style={styles.cell}>
                            {renderAlsoSentTo(receivedCase)}
                        </td>
                        <td style={styles.cell}>
                            {renderStatus(receivedCase)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </CardTable>
        {confirmRejectCaseSentId && (
            <ConfirmationModal
                isOpen={!!confirmRejectCaseSentId}
                onClose={() => setConfirmRejectCaseSentId(null)}
                onConfirm={async () => {
                    // The modal closes itself (onClose) when this settles.
                    const note = rejectNote.trim();
                    await onRejectClick(confirmRejectCaseSentId, note || undefined);
                }}
                title="Reject this case?"
                message="The sender will see that your team rejected it. You can no longer accept it after you reject it."
                confirmText="Reject"
                cancelText="Cancel"
                confirmButtonStyle="danger"
            >
                <label htmlFor="reject-case-note" style={{ display: 'block', marginTop: theme.spacing.md, marginBottom: theme.spacing.xs, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                    Note (optional)
                </label>
                <textarea
                    id="reject-case-note"
                    data-testid="reject-case-note"
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    maxLength={1000}
                    rows={3}
                    placeholder="Reason for rejecting"
                    style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: theme.spacing.sm,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.border.default}`,
                        backgroundColor: theme.colors.surface,
                        color: theme.colors.text.primary,
                        fontSize: theme.typography.fontSize.sm,
                        resize: 'vertical',
                    }}
                />
            </ConfirmationModal>
        )}
        </div>
    );
};

